/**
 * SessionSummary — shown at the end of a practice session.
 *
 * Reports accuracy, items reviewed, and time taken. Optionally highlights
 * any items newly reaching the "mastered" tier.
 */

import { type ReactNode } from "react";

export interface RatingCounts {
  again: number;
  hard: number;
  good: number;
  easy: number;
}

export interface SessionSummaryProps {
  itemsReviewed: number;
  correctCount: number;
  durationSeconds: number;
  newlyMastered?: string[];
  /** FSRS rating breakdown. Shown only when at least one non-easy rating exists. */
  ratingCounts?: RatingCounts;
  onContinue: () => void;
  onReturnHome: () => void;
  onFeedback?: () => void;
}

export function SessionSummary({
  itemsReviewed,
  correctCount,
  durationSeconds,
  newlyMastered = [],
  ratingCounts,
  onContinue,
  onReturnHome,
  onFeedback,
}: SessionSummaryProps): ReactNode {
  const accuracy = itemsReviewed > 0 ? Math.round((correctCount / itemsReviewed) * 100) : 0;
  const mins = Math.floor(durationSeconds / 60);
  const secs = Math.floor(durationSeconds % 60);

  return (
    <section className="bg-paper border border-ink/20 p-8 max-w-lg mx-auto" aria-label="Session summary">
      <h2 className="font-serif italic text-3xl font-medium text-ink mb-1">Session complete.</h2>
      <p className="font-serif italic text-lg text-inkSoft mb-6">Nice work.</p>

      <dl className="grid grid-cols-3 gap-0 border border-ink/10 mb-6">
        <Stat label="Accuracy" value={`${accuracy}%`} />
        <Stat label="Items" value={`${itemsReviewed}`} bordered />
        <Stat label="Time" value={`${mins}:${secs.toString().padStart(2, "0")}`} bordered />
      </dl>

      {newlyMastered.length > 0 && (
        <div className="bg-success/10 border-l-2 border-success p-4 mb-6">
          <p className="text-sm font-medium text-success mb-1">Newly mastered</p>
          <ul className="text-sm text-inkSoft list-none">
            {newlyMastered.map((name) => (
              <li key={name} className="font-serif italic">
                — {name}
              </li>
            ))}
          </ul>
        </div>
      )}

      {ratingCounts && (ratingCounts.again + ratingCounts.hard + ratingCounts.good) > 0 && (
        <div className="flex gap-3 flex-wrap font-mono text-xs mb-6" aria-label="Rating breakdown">
          {ratingCounts.easy > 0 && (
            <span className="text-success">{ratingCounts.easy} easy</span>
          )}
          {ratingCounts.good > 0 && (
            <span className="text-success">{ratingCounts.good} good</span>
          )}
          {ratingCounts.hard > 0 && (
            <span className="text-inkSoft">{ratingCounts.hard} hard</span>
          )}
          {ratingCounts.again > 0 && (
            <span className="text-danger">{ratingCounts.again} again</span>
          )}
        </div>
      )}

      <div className="flex gap-3">
        <button
          type="button"
          onClick={onContinue}
          className="flex-1 py-3 px-5 bg-ink text-paper hover:bg-accent transition-colors"
        >
          Practice another
        </button>
        <button
          type="button"
          onClick={onReturnHome}
          className="flex-1 py-3 px-5 bg-transparent border border-ink/20 hover:bg-ink hover:text-paper transition-colors"
        >
          Back to home
        </button>
      </div>
      {onFeedback && (
        <div className="mt-4 text-center">
          <button
            type="button"
            onClick={onFeedback}
            className="font-mono text-xs text-inkFaded hover:text-ink transition-colors"
          >
            leave feedback →
          </button>
        </div>
      )}
    </section>
  );
}

function Stat({ label, value, bordered }: { label: string; value: string; bordered?: boolean }): ReactNode {
  return (
    <div className={`p-4 text-center ${bordered ? "border-l border-ink/10" : ""}`}>
      <dt className="text-[10px] uppercase tracking-widest text-inkFaded mb-1 font-medium">
        {label}
      </dt>
      <dd className="font-serif text-3xl font-medium text-ink tabular-nums">{value}</dd>
    </div>
  );
}
